import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import StudentDetail from "@/components/admin/StudentDetail";

const LEVELS = ["beginner", "elementary", "intermediate", "upper_intermediate", "advanced", "proficient"];

export default function StudentsTab() {
  const { t } = useLang();
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", level: "beginner", goal: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/students").then((r) => setStudents(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const createStudent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/students", form);
      toast.success(t("admin.studentCreated"));
      setAddOpen(false);
      setForm({ name: "", email: "", password: "", level: "beginner", goal: "", phone: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const removeStudent = async () => {
    const victim = toDelete;
    try {
      await api.delete(`/admin/students/${victim.id}`);
      toast.success(t("admin.studentRemoved"));
      setToDelete(null);
      if (selected?.id === victim.id) setSelected(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="students-tab">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif font-semibold text-lg">{t("admin.students")}</h2>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-student-btn">
          <Plus className="w-4 h-4 mr-1" /> {t("admin.addStudent")}
        </Button>
      </div>

      <div className="mt-4 space-y-3">
        {students.length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
        {students.map((s) => (
          <Card key={s.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(s)} data-testid={`student-row-${s.id}`}>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">{s.name}</p>
                <p className="text-sm text-muted-foreground">{s.email}</p>
                {s.goal && <p className="text-xs text-muted-foreground mt-0.5">{s.goal}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="secondary" className="capitalize" data-testid={`student-level-${s.id}`}>{t(`levels.items.${s.level || "beginner"}`)}</Badge>
                <Badge variant="outline">{t("admin.completedLessons")}: {s.lessons_completed}</Badge>
                <Badge variant="outline">{t("admin.remainingLessons")}: {s.remaining_lessons}</Badge>
                {s.attendance != null && <Badge variant="outline">{t("dash.attendance")}: {s.attendance}%</Badge>}
                {s.quiz_avg != null && <Badge variant="outline">{t("admin.quizAvg")}: {s.quiz_avg}%</Badge>}
                <Badge className="bg-amber-100 text-amber-900">{t("admin.milestonesLabel")}: {s.milestones_done}/{s.milestones_total}</Badge>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setToDelete(s); }}
                className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                data-testid={`student-delete-${s.id}`}
                aria-label={t("admin.removeStudentTitle")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-student-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("admin.addStudent")}</DialogTitle></DialogHeader>
          <form onSubmit={createStudent} className="space-y-3">
            <div className="space-y-1"><Label>{t("auth.name")}</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-student-name" /></div>
            <div className="space-y-1"><Label>{t("auth.email")}</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-student-email" /></div>
            <div className="space-y-1"><Label>{t("auth.password")}</Label>
              <Input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-student-password" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>{t("admin.levelLabel")}</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="new-student-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`levels.items.${l}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>{t("admin.phoneLabel")}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-student-phone" /></div>
            </div>
            <div className="space-y-1"><Label>{t("admin.goalLabel")}</Label>
              <Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="new-student-goal" /></div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="create-student-submit">{t("admin.addStudent")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-student-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("admin.removeStudentTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="delete-student-text">
            <strong>{toDelete?.name}</strong> ({toDelete?.email}) — {t("admin.removeStudentConfirm")}
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setToDelete(null)} data-testid="delete-student-cancel">{t("admin.cancel")}</Button>
            <Button variant="destructive" onClick={removeStudent} data-testid="delete-student-confirm">{t("admin.delete")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="student-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl">{selected?.name}</SheetTitle>
          </SheetHeader>
          {selected && (
            <StudentDetail
              studentId={selected.id}
              onChanged={load}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
